<template>
  <div class="promo">
    <div class="promo__container _container">
      <!-- Акция не настроена -->
      <div v-if="notAvailable || status === 'unavailable'" class="promo__empty">
        {{ $t("promo.notAvailable") }}
      </div>

      <template v-else-if="ticket">
        <h1 class="promo__title">{{ $t("promo.title") }}</h1>

        <!-- Активна: таймер до конца + остаток -->
        <div class="promo__meters" v-if="status === 'active'">
          <div class="promo__counter">
            <span class="promo__counter-fire">⏰</span>
            <span class="promo__counter-label">{{ countdownLabel }}</span>
            <span class="promo__counter-num">{{ countdown }}</span>
          </div>
          <div class="promo__tickets">
            🔥 {{ $t("promo.ticketsLeft") }}
            <b>{{ remaining }}</b>
          </div>
        </div>

        <!-- Ещё не началась: продажи откроются в HH:MM -->
        <div class="promo__state _soon" v-else-if="status === 'not_started'">
          <span class="promo__state-icon">🕘</span>
          <div class="promo__state-title">
            {{ $t("promo.salesOpenAt") }} <b>{{ startTime }}</b>
          </div>
          <div class="promo__state-timer" v-if="countdown">⏳ {{ countdown }}</div>
        </div>

        <!-- Раскуплено на сегодня -->
        <div class="promo__state _out" v-else-if="status === 'ended'">
          <span class="promo__state-icon">🎟️</span>
          <div class="promo__state-title">{{ $t("promo.soldOutToday") }}</div>
          <div class="promo__state-sub">{{ $t("promo.comeBackTomorrow") }}</div>
        </div>

        <div class="promo__card">
          <div class="promo__card-name">{{ ticket.ticket_title[$lang] }}</div>

          <p
            class="promo__card-desc"
            v-if="ticket.short_description && ticket.short_description[$lang]"
          >
            {{ ticket.short_description[$lang] }}
          </p>

          <div class="promo__row">
            <div class="promo__row-type">{{ ticket.adult_ticket[$lang] }}</div>
            <div class="promo__row-price">
              {{ formatPrice(ticket.adult_actual_price) }}
            </div>
          </div>
          <div class="promo__row">
            <div class="promo__row-type">{{ ticket.child_ticket[$lang] }}</div>
            <div class="promo__row-price _accent">
              {{ formatPrice(childPrice) }}
            </div>
          </div>

          <div class="promo__total">
            <span>{{ $t("promo.total") }}</span>
            <span>{{ formatPrice(total) }}</span>
          </div>

          <!-- Нужен вход (только когда акция активна) -->
          <button
            v-if="status === 'active' && !isLoggedIn"
            class="promo__button"
            @click="goLogin"
          >
            {{ $t("promo.loginToBuy") }}
          </button>

          <!-- Оплата -->
          <div v-else-if="status === 'active'" class="promo__pay">
            <label class="promo__pay-label">{{
              $t("basket.paymentType")
            }}</label>
            <SelectComponent
              class="promo__dropdown"
              :selected="selectedPayment"
              :options="options"
              @select="selectOption"
            />

            <label class="promo__agree" v-if="selectedPayment.id">
              <input type="checkbox" v-model="checked" />
              <span>{{ $t("promo.agree") }}</span>
              <router-link to="/rule/payment" target="_blank">{{
                $t("oznText")
              }}</router-link>
            </label>

            <button
              class="promo__button"
              @click.prevent="buyTicket"
              :disabled="paying"
            >
              {{ $t("basket.pay") }} {{ formatPrice(total) }}
            </button>
          </div>

          <!-- large_description приходит из админки как HTML (редактор), рендерим разметку -->
          <div
            class="promo__card-about"
            v-if="ticket.large_description && ticket.large_description[$lang]"
            v-html="ticket.large_description[$lang]"
          ></div>
        </div>
      </template>

      <div v-else class="promo__empty">…</div>
    </div>

    <!-- Kaspi QR модалка -->
    <ModalComponent :showModal="showModal" @hideModal="hideModal">
      <div class="promo__qr">
        <img :src="kaspiQrSrc" alt="Kaspi QR" />
        <h2>{{ $t("vam") }}</h2>
      </div>
    </ModalComponent>
  </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";
import SelectComponent from "@/components/SelectComponent";
import ModalComponent from "@/components/ModalComponent";
import { showHalykWidget } from "@/utils/halykWidget";

export default {
  name: "HappyHour",
  components: { SelectComponent, ModalComponent },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      ticket: null,
      childPrice: 50,
      remaining: null,
      limit: 0,
      status: "", // active | not_started | ended | unavailable
      startMs: null,
      endMs: null,
      startRaw: null, // ISO начала окна (для вывода "откроются в HH:MM" в тайм-зоне сервера)
      offset: 0, // server_time - client_time (мс), против расхождения часов
      now: Date.now(),
      notAvailable: false,
      paying: false,
      checked: false,
      showModal: false,
      kaspiInfo: {},
      tickTimer: null,
      pollTimer: null,
      options: [
        {
          id: 1,
          name: "Kaspi QR",
          value: "kaspi",
          icon: require("@/assets/icons/payment/kaspi.svg"),
        },
        {
          id: 2,
          name: this.$t("basket.typeCard"),
          value: "halyk",
          icon: require("@/assets/icons/payment/card.svg"),
        },
        {
          id: 3,
          name: "Kaspi",
          value: "mobile",
          icon: require("@/assets/icons/payment/kaspi.svg"),
        },
      ],
      selectedPayment: { name: this.$t("basket.changeType") },
    };
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem("access_token");
    },
    total() {
      if (!this.ticket) return 0;
      return Number(this.ticket.adult_actual_price) + Number(this.childPrice);
    },
    targetMs() {
      return this.status === "not_started" ? this.startMs : this.endMs;
    },
    countdownLabel() {
      return this.status === "not_started"
        ? this.$t("promo.tillStart")
        : this.$t("promo.tillEnd");
    },
    // "HH:MM" из ISO окна — берём часы:минуты как их задал админ (тайм-зона сервера),
    // не зависит от часового пояса браузера.
    startTime() {
      return this.startRaw ? this.startRaw.slice(11, 16) : "";
    },
    msLeft() {
      if (!this.targetMs) return 0;
      return this.targetMs - (this.now + this.offset);
    },
    countdown() {
      let s = Math.max(0, Math.floor(this.msLeft / 1000));
      const h = Math.floor(s / 3600);
      s -= h * 3600;
      const m = Math.floor(s / 60);
      s -= m * 60;
      const pad = (n) => String(n).padStart(2, "0");
      return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
    },
    kaspiQrSrc() {
      return `https://admin.san-remo.kz/api/V1/qr-code/${this.kaspiInfo.qr_token}`;
    },
  },
  created() {
    this.fetchHappyHour();
  },
  mounted() {
    this.tickTimer = setInterval(this.tick, 1000);
    // подстраховка: раз в минуту синхронизируем остаток/статус с сервером
    this.pollTimer = setInterval(this.fetchHappyHour, 60000);
  },
  beforeUnmount() {
    if (this.tickTimer) clearInterval(this.tickTimer);
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  watch: {
    showModal(val) {
      document.body.style.overflow = val ? "hidden" : "auto";
    },
  },
  methods: {
    fetchHappyHour() {
      axios
        .get("V1/happy-hour-ticket")
        .then((res) => {
          const d = res.data;
          this.ticket = d.ticket;
          this.childPrice = d.child_price;
          this.remaining = d.remaining;
          this.limit = d.limit;
          this.status = d.status;
          this.startRaw = d.start || null;
          this.startMs = d.start ? new Date(d.start).getTime() : null;
          this.endMs = d.end ? new Date(d.end).getTime() : null;
          this.offset = d.server_time
            ? new Date(d.server_time).getTime() - Date.now()
            : 0;
          this.notAvailable = false;
        })
        .catch((err) => {
          if (err.response && err.response.status === 404) {
            this.notAvailable = true;
          } else {
            console.error(err);
          }
        });
    },
    tick() {
      this.now = Date.now();
      // окно закрылось локально -> синхронизируемся с сервером (сменит статус)
      if (
        (this.status === "active" || this.status === "not_started") &&
        this.msLeft <= 0
      ) {
        this.fetchHappyHour();
      }
    },
    goLogin() {
      localStorage.setItem("redirect_after_auth", "/happy-hour");
      this.$router.push("/login");
    },
    selectOption(option) {
      this.selectedPayment = option;
    },
    formatPrice(currency) {
      return currency.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " ₸";
    },
    hideModal(isOpen) {
      this.showModal = isOpen;
    },
    buyTicket() {
      if (this.status !== "active") {
        this.toast.warning(this.$t("promo.ended"));
        this.fetchHappyHour();
        return;
      }
      if (this.selectedPayment.id == null) {
        this.toast.warning(this.$t("basket.changeType"));
        return;
      }
      if (!this.checked) {
        this.toast.warning(this.$t("login.paymentRules"));
        return;
      }
      this.paying = true;
      axios
        .get("V1/payment", {
          params: {
            bank: this.selectedPayment.value,
            amount: this.total,
            ticket_id_1: this.ticket.id,
            adult_num_1: 1,
            child_num_1: 1,
            promo_full_day_1: true,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })
        .then((response) => {
          if (response.data && response.data.error) {
            this.handlePromoError(response.data.error);
            return;
          }
          this.handlePayment(response);
        })
        .catch((error) => {
          const data = error.response && error.response.data;
          if (data && data.error) {
            this.handlePromoError(data.error);
          } else {
            console.error(error);
            this.toast.error(this.$t("promo.payError"));
          }
        })
        .finally(() => {
          this.paying = false;
        });
    },
    handlePromoError(code) {
      if (code === "happy_hour_sold_out") {
        this.toast.warning(this.$t("promo.soldOut"));
      } else if (code === "happy_hour_closed") {
        this.toast.warning(this.$t("promo.ended"));
      } else if (code === "promo_already_purchased") {
        this.toast.warning(this.$t("promo.alreadyBought"));
      } else {
        this.toast.error(this.$t("promo.payError"));
      }
      this.fetchHappyHour();
    },
    handlePayment(response) {
      if (this.selectedPayment.id == 2) {
        showHalykWidget(response.data, this.total, {
          backLink: "https://san-remo.kz/personal-account",
          failureBackLink: "https://san-remo.kz/happy-hour",
          description: "Оплата акции 'Счастливый час'",
        });
      } else if (this.selectedPayment.id == 1) {
        this.kaspiInfo = response.data;
        this.showModal = true;
        window.scroll(0, 0);
        setTimeout(() => {
          this.showModal = false;
        }, this.kaspiInfo.expire_time * 1000);
        this.checkStatus(this.kaspiInfo);
      } else {
        const kaspiShop = response.data;
        window.location.href = kaspiShop.payment_link;
        this.checkStatus(kaspiShop);
      }
    },
    checkStatus(kaspi) {
      const startTime = new Date().getTime();
      const interval = setInterval(() => {
        if (new Date().getTime() - startTime < kaspi.confirm_time * 1000) {
          axios
            .get("V1/statusqr", { params: { QrPaymentId: kaspi.invoice_id } })
            .then((response) => {
              if (response.data.Data.Status == "Processed") {
                clearInterval(interval);
                this.showModal = false;
                this.$router.push("/personal-account");
              }
            })
            .catch((error) => {
              console.error("There was an error!", error);
            });
        } else {
          clearInterval(interval);
        }
      }, kaspi.refresh_time * 1000);
    },
  },
};
</script>

<style lang="scss" scoped>
.promo {
  padding: 60px 0 100px;
  font-family: "Nunito";

  &__container {
    max-width: 720px;
  }

  &__title {
    font-weight: 800;
    font-size: 44px;
    line-height: 1.1;
    color: #256ed8;
    text-align: center;
    margin-bottom: 28px;

    @media (max-width: 767.98px) {
      font-size: 30px;
    }
  }

  &__meters {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    margin-bottom: 30px;
  }

  &__counter {
    width: fit-content;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 32px;
    border-radius: 999px;
    background: linear-gradient(135deg, #ff8a3d 0%, #ff5722 100%);
    color: #ffffff;
    box-shadow: 0 10px 26px rgba(255, 87, 34, 0.4);

    &-fire {
      font-size: 24px;
      line-height: 1;
    }

    &-label {
      font-weight: 700;
      font-size: 20px;
      letter-spacing: 0.01em;
    }

    &-num {
      font-weight: 900;
      font-size: 34px;
      line-height: 1;
      font-variant-numeric: tabular-nums;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
    }

    @media (max-width: 767.98px) {
      gap: 8px;
      padding: 11px 22px;

      &-fire {
        font-size: 20px;
      }
      &-label {
        font-size: 16px;
      }
      &-num {
        font-size: 27px;
      }
    }
  }

  &__tickets {
    font-weight: 700;
    font-size: 20px;
    color: #16a34a;

    b {
      font-weight: 900;
      font-size: 24px;
    }

    @media (max-width: 767.98px) {
      font-size: 16px;
      b {
        font-size: 20px;
      }
    }
  }

  &__state {
    max-width: 540px;
    margin: 0 auto 30px;
    text-align: center;
    padding: 30px 34px;
    border-radius: 28px;
    background: #f0f6ff;
    border: 2px solid #cfe0ff;
    box-shadow: 0 10px 30px rgba(37, 99, 235, 0.12);

    &-icon {
      display: block;
      font-size: 44px;
      line-height: 1;
      margin-bottom: 12px;
    }

    &-title {
      font-weight: 800;
      font-size: 24px;
      line-height: 1.25;
      color: #2563eb;

      b {
        font-weight: 900;
        color: #1d4ed8;
      }
    }

    &-timer {
      margin-top: 12px;
      display: inline-block;
      padding: 6px 18px;
      border-radius: 999px;
      background: #ffffff;
      font-weight: 800;
      font-size: 22px;
      color: #2563eb;
      font-variant-numeric: tabular-nums;
      box-shadow: 0 4px 14px rgba(37, 99, 235, 0.15);
    }

    &-sub {
      margin-top: 10px;
      font-weight: 600;
      font-size: 16px;
      color: #64748b;
    }

    &._out {
      background: #fff5f4;
      border-color: #ffd7d3;
      box-shadow: 0 10px 30px rgba(255, 84, 71, 0.12);

      .promo__state-title {
        color: #ff5447;
      }
    }

    @media (max-width: 767.98px) {
      padding: 24px 18px;
      border-radius: 22px;

      &-icon {
        font-size: 34px;
      }
      &-title {
        font-size: 19px;
      }
      &-timer {
        font-size: 18px;
      }
      &-sub {
        font-size: 14px;
      }
    }
  }

  &__card {
    background: #ffffff;
    border: 3px solid #ff8a3d;
    border-radius: 40px;
    padding: 40px;
    box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.08);

    @media (max-width: 767.98px) {
      padding: 24px 16px;
      border-radius: 24px;
    }
  }

  &__card-name {
    font-weight: 800;
    font-size: 30px;
    color: #ff6b6b;
    margin-bottom: 8px;

    @media (max-width: 767.98px) {
      font-size: 22px;
    }
  }

  &__card-desc {
    font-size: 18px;
    line-height: 1.5;
    font-weight: 600;
    color: #4a4a4a;
    margin-bottom: 16px;

    @media (max-width: 767.98px) {
      font-size: 16px;
    }
  }

  &__card-about {
    font-size: 16px;
    line-height: 1.6;
    color: #626262;
    background: #fff6ef;
    border-left: 4px solid #ff8a3d;
    border-radius: 14px;
    padding: 16px 18px;
    margin-top: 24px;
    margin-bottom: 24px;

    // разметка из админского HTML (v-html) — scoped-стили пробрасываем через :deep
    :deep(p) {
      margin: 0 0 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(ul),
    :deep(ol) {
      margin: 0 0 8px;
      padding-left: 20px;
    }

    :deep(a) {
      color: #ff6b6b;
      text-decoration: underline;
    }

    @media (max-width: 767.98px) {
      font-size: 15px;
      padding: 14px;
      border-radius: 12px;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 0;
    border-bottom: 1px dashed #d9d9d9;

    &-type {
      font-weight: 600;
      font-size: 20px;
      color: #2c3131;

      @media (max-width: 767.98px) {
        font-size: 16px;
      }
    }

    &-price {
      font-weight: 700;
      font-size: 22px;
      color: #00bed6;

      &._accent {
        color: #ff6b6b;
      }

      @media (max-width: 767.98px) {
        font-size: 18px;
      }
    }
  }

  &__total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 24px 0;
    font-weight: 800;
    font-size: 26px;
    color: #256ed8;

    @media (max-width: 767.98px) {
      font-size: 20px;
    }
  }

  &__pay {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__dropdown {
    position: relative;
    z-index: 5;

    :deep(.select__text span),
    :deep(.select__list span) {
      width: 30px;
      height: 30px;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    // Как в корзине: десктоп — Kaspi QR + Оплата картой,
    // мобилка — Оплата картой + Kaspi (ссылка в приложение)
    :deep(.select__list) {
      li:last-child {
        @media (min-width: $mobile + px) {
          display: none;
        }
      }

      li:first-child {
        @media (max-width: $mobile + px) {
          display: none;
        }
      }
    }
  }

  &__pay-label {
    font-weight: 700;
    font-size: 20px;
    color: #256ed8;
  }

  &__agree {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 16px;
    color: #256ed8;
    cursor: pointer;

    input {
      width: 20px;
      height: 20px;
    }

    a {
      text-decoration: underline;
    }
  }

  &__button {
    width: 100%;
    background: #256ed8;
    border-radius: 15px;
    padding: 18px 0;
    font-weight: 700;
    font-size: 20px;
    color: #ffffff;
    transition: 0.2s;

    &:hover {
      background: #1b57ac;
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__empty {
    text-align: center;
    font-size: 24px;
    color: #626262;
    padding: 80px 0;
  }

  &__qr {
    padding: 50px;
    display: flex;
    flex-direction: column;
    align-items: center;

    img {
      width: 250px;
      height: 250px;
    }

    h2 {
      margin-top: 16px;
      font-size: 20px;
      color: #2c3131;
    }
  }
}
</style>
